import React, { useEffect, useRef, useState } from 'react';

const useCountUpOnVisible = (end: number, duration: number = 2000) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current || hasAnimated) return;

      const rect = ref.current.getBoundingClientRect();
      const isVisible = rect.top <= window.innerHeight && rect.bottom >= 0;

      if (isVisible) {
        setHasAnimated(true);
        let start = 0;
        const increment = end / (duration / 16);

        const animate = () => {
          start += increment;
          if (start < end) {
            setCount(Math.floor(start));
            requestAnimationFrame(animate);
          } else {
            setCount(end);
          }
        };

        animate();
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // run on mount

    return () => window.removeEventListener('scroll', handleScroll);
  }, [end, duration, hasAnimated]);

  return { ref, count };
};

const ImpactSection = () => {
  const homes = useCountUpOnVisible(8613, 1000); // 1 second
  const savings = useCountUpOnVisible(4300000, 1000);
  const co2 = useCountUpOnVisible(42800, 1000);

  return (
    <div
      className="w-full px-6 py-8 text-center lg:py-16 xl:px-44"
      style={{
        background: `linear-gradient(89deg, rgba(231, 111, 81, 0.16) -3.48%, rgba(244, 162, 97, 0.13) 25.55%, rgba(233, 196, 105, 0.11) 52.25%, rgba(42, 156, 142, 0.10) 77.27%, rgba(13, 105, 92, 0.06) 103.3%)`,
      }}
    >
      <div className="mx-auto max-w-7xl">
        <p className="mb-4 text-xs font-extrabold uppercase tracking-wider text-[#516B75] md:mb-0 lg:text-lg">
          Making a Real Impact
        </p>
        <h2 className="text-2xl font-bold lg:text-5xl">
          Together, we’re building a{' '}
          <span className="font-kalam text-nowrap bg-gradient-to-l from-[#84AF7D] via-[#289A8C] to-[#214D55] bg-clip-text text-[32px] font-bold lowercase leading-[48px] text-transparent lg:text-[64px] lg:leading-[80px]">
            greener future
          </span>
        </h2>
        <div className="mt-4 flex flex-col items-center gap-2 md:grid md:grid-cols-3 lg:mt-10 lg:gap-6">
          <div
            ref={homes.ref}
            className="my-auto flex w-full max-w-[185px] flex-col items-center justify-center rounded-[10px] bg-white py-4 text-center md:w-auto md:max-w-[360px] md:px-12 lg:min-h-[118px]"
          >
            <h2 className="text-nowrap text-2xl font-bold lg:text-[48px] lg:leading-[48px]">
              {homes.count.toLocaleString()}
            </h2>
            <p className="text-[14px] lg:mt-2 lg:text-[16px]">Homes improved</p>
          </div>
          <div
            ref={savings.ref}
            className="my-auto flex w-full max-w-[185px] flex-col items-center justify-center rounded-[10px] bg-white py-4 text-center md:w-auto md:max-w-[360px] md:px-12 lg:min-h-[118px]"
          >
            <h2 className="text-nowrap text-2xl font-bold  lg:text-[48px] lg:leading-[48px]">
              £{(savings.count / 1000000).toFixed(1)} M
            </h2>
            <p className="text-[14px] lg:mt-2 lg:text-[16px]">Saved on energy bills</p>
          </div>
          <div
            ref={co2.ref}
            className="my-auto flex w-full max-w-[185px] flex-col items-center justify-center rounded-[10px] bg-white py-4 text-center md:w-auto md:max-w-[360px] md:px-12 lg:min-h-[118px]"
          >
            <h2 className="text-nowrap text-2xl font-bold  lg:text-[48px] lg:leading-[48px]">
              {co2.count.toLocaleString()} T
            </h2>
            <p className="text-[14px] lg:mt-2 lg:text-[16px]">CO2 saved</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpactSection;
